import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { LegalLinks } from '@/components/legal/LegalLinks';

/**
 * Login screen · slot_ux_polish UXP.3 redesign (2026-05-23 · Genie-approved).
 *
 * Bespoke editorial RISO layout — split: cream editorial panel (display type +
 * decorative chips) · dark form panel. Replaces the former shared-AuthShell
 * centred-card layout. Per Genie picks 23 MAY: OAuth buttons hidden (accounts
 * are invite-only · GoogleButton/YandexButton kept in the codebase, just not
 * rendered here), self-serve signup removed (accounts arrive by magic-link
 * invitation · CF-UXP-1). Auth LOGIC is unchanged — the `auth.login` call and
 * the component state are identical to the pre-redesign screen.
 *
 * `FormField` is still exported from this file — Signup / ForgotPassword /
 * ResetPassword import it. The Login form itself uses bespoke underline inputs.
 */

function emailLooksValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function translateAuthError(raw: string): string {
  if (/unable to validate email/i.test(raw)) return 'Email в неверном формате';
  if (/invalid login credentials/i.test(raw)) return 'Неверный email или пароль';
  if (/rate limit/i.test(raw)) return 'Слишком много попыток — попробуй через минуту';
  if (/network|fetch failed/i.test(raw)) return 'Не удалось связаться с сервером';
  return raw;
}

export interface LoginScreenProps {
  onSuccess: () => void;
  /**
   * Kept for router compatibility · self-serve signup is retired (accounts are
   * invite-only via magic link · CF-UXP-1). No on-screen affordance uses it.
   */
  /** Optional · self-serve signup retired (invite-only · CF-UXP-1 · 2026-05-30). */
  onSwitchSignup?: () => void;
  onForgotPassword: () => void;
}

export function LoginScreen({ onSuccess, onForgotPassword }: LoginScreenProps) {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (!emailLooksValid(trimmedEmail)) {
      setError('Введи корректный email');
      return;
    }
    if (!password) {
      setError('Введи пароль');
      return;
    }
    setSubmitting(true);
    const res = await auth.login({ email: trimmedEmail, password });
    if ('error' in res) {
      setError(translateAuthError(res.error));
      setSubmitting(false);
      return;
    }
    onSuccess();
  };

  return (
    <div className="login2">
      <aside className="login2__editorial">
        <div className="login2__brand">
          <span className="login2__brand-dot">QA</span>
          <span className="login2__brand-tag">QA Studio · 2026</span>
        </div>
        <p className="login2__eyebrow">— Тестирование · реальные задачи</p>
        <h1 className="login2__display">
          Тестируй<br />
          <span className="login2__display-accent">на</span>стоящий.
        </h1>
        <p className="login2__manifest">
          // уроки, практика
          <br />
          // на боевых тикетах → не на учебных
        </p>
        <div className="login2__chips" aria-hidden="true">
          <span className="login2__chip login2__chip--live">● Sprint 03</span>
          <span className="login2__chip">DOC-03</span>
          <span className="login2__chip">BUG-127 · P0</span>
        </div>
      </aside>

      <main className="login2__panel" id="main">
        <div className="login2__meta">
          <span>_auth · v2</span>
          <span>Sprint 03 · день 4/10</span>
        </div>

        <div className="login2__body">
          <h2 className="login2__title">Добро пожаловать.</h2>
          <p className="login2__subtitle">
            // войди, чтобы продолжить{' '}
            <span className="login2__subtitle-accent">Sprint 03</span>
          </p>

          <form className="login2__form" onSubmit={handleSubmit} noValidate data-testid="login-form">
            <div className="login2__field">
              <label htmlFor="login-email" className="login2__field-label">
                Email
              </label>
              <input
                id="login-email"
                className="login2__input"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="login2__field">
              <div className="login2__field-head">
                <label htmlFor="login-password" className="login2__field-label">
                  Пароль
                </label>
                <button type="button" className="login2__forgot" onClick={onForgotPassword}>
                  Забыли пароль?
                </button>
              </div>
              <div className="pw-wrap">
                <input
                  id="login-password"
                  className="login2__input pw-has-reveal"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  maxLength={128}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordReveal revealed={showPw} onToggle={() => setShowPw((v) => !v)} />
              </div>
            </div>

            <div className="login2__error-slot" aria-live="polite">
              {error && (
                <p className="login2__error" role="alert" data-testid="login-error">
                  {error}
                </p>
              )}
            </div>

            <button type="submit" className="login2__submit" disabled={submitting}>
              <span>{submitting ? 'Входим…' : 'Войти и продолжить'}</span>
              <span className="login2__submit-arrow" aria-hidden="true">
                →
              </span>
            </button>
          </form>

          <div className="login2__hint" aria-hidden="true">
            <span className="login2__hint-tag">BUG-127</span>
            <span className="login2__hint-text">
              <span className="login2__hint-title">Чек-аут падает на iPhone SE при СБП</span>
              <span className="login2__hint-meta">● P0 · ждёт тебя · 2 ч</span>
            </span>
          </div>
        </div>

        <div className="login2__foot">
          <span>© QA Studio</span>
          <span>// вход только по приглашению</span>
        </div>
        <LegalLinks />
      </main>
    </div>
  );
}

interface FormFieldProps {
  id: string;
  label: string;
  type: string;
  autoComplete?: string;
  required?: boolean;
  maxLength?: number;
  value: string;
  onChange: (next: string) => void;
}

/** Eye / eye-off glyph for the password reveal toggle (inline · no icon dep). */
function EyeGlyph({ off }: { off: boolean }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  return off ? (
    <svg {...common}>
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.4 18.4 0 0 1-2.16 3.19M6.6 6.6A18.4 18.4 0 0 0 2 12s3 8 10 8a9.1 9.1 0 0 0 5.4-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  ) : (
    <svg {...common}>
      <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * Password reveal toggle · default masked, manual click to peek (Genie 14 AUG).
 * Because it defaults to masked and only reveals on an explicit click, browser
 * autofill fills the field masked and never auto-exposes a saved credential.
 * Keyboard-operable (real <button>, kept in tab order).
 */
export function PasswordReveal({ revealed, onToggle }: { revealed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="pw-reveal"
      onClick={onToggle}
      aria-label={revealed ? 'Скрыть пароль' : 'Показать пароль'}
      aria-pressed={revealed}
    >
      <EyeGlyph off={revealed} />
    </button>
  );
}

export function FormField({ id, label, type, autoComplete, required, maxLength, value, onChange }: FormFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="auth-form__field">
      <label htmlFor={id} className="auth-form__label">
        {label}
      </label>
      <div className="pw-wrap">
        <input
          id={id}
          type={isPassword && revealed ? 'text' : type}
          autoComplete={autoComplete}
          required={required}
          maxLength={maxLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`auth-form__input${isPassword ? ' pw-has-reveal' : ''}`}
        />
        {isPassword && <PasswordReveal revealed={revealed} onToggle={() => setRevealed((v) => !v)} />}
      </div>
    </div>
  );
}
